import { useState, useEffect } from "react";
import { auth, db, functions } from "./firebase";
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from "firebase/auth";
import { collectionGroup, query, where, doc, updateDoc, serverTimestamp, onSnapshot, setDoc, deleteDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import LoginPage from "./components/LoginPage";
import Sidebar from "./components/Sidebar";
import MetricsGrid from "./components/MetricsGrid";
import SubmissionTable from "./components/SubmissionTable";
import SessionCreator, { REQUIRED_SLOTS, projectCode, stripPidPrefix } from "./components/SessionCreator";
import SessionWorkspace from "./components/SessionWorkspace";
import BatchUpload from "./components/BatchUpload";
import OutdoorGuide from "./components/OutdoorGuide";
import { C } from "./styles";
import { Btn, SP, RAD, eyebrow } from "./ui";

// Per-project icons for the home project picker; falls back to 🎬.
const PROJECT_ICONS = {
  "Cooking": "🍳",
  "Proactive Cooking": "🥘",
  "Appliance data": "🔌",
  "Outdoor Guide": "🏞️",
};
const projectIcon = (p) => PROJECT_ICONS[p] || "🎬";

// Projects that should never appear in the picker/dropdowns even if a creator's
// Notion record still lists them (legacy/unsupported). Matched case-insensitively.
const HIDDEN_PROJECTS = new Set(["diy", "cityguide", "city guide"]);
const visibleProjects = (list) =>
  (list || []).filter((p) => !HIDDEN_PROJECTS.has(String(p).trim().toLowerCase()));

function App() {
  const [user, setUser] = useState(null);
  const [pid, setPid] = useState(null);
  const [userName, setUserName] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [accessError, setAccessError] = useState("");
  const [projects, setProjects] = useState([]);
  const [batchUploader, setBatchUploader] = useState(false); // Notion feature flag
  const [isAdmin, setIsAdmin] = useState(false); // Notion "Admin" flag
  // Admin "View as user" support tool. realIdentity holds the admin's own
  // identity so we can restore it on exit; viewAs holds the target {pid,name}
  // while active. The pid/userName/projects state below are the EFFECTIVE
  // values — swapped to the target so the existing subscription + UI render
  // as that user with no other plumbing.
  const [realIdentity, setRealIdentity] = useState(null);
  const [viewAs, setViewAs] = useState(null); // { pid, name } | null
  const [viewAsInput, setViewAsInput] = useState("");
  const [viewAsLoading, setViewAsLoading] = useState(false);
  const [viewAsError, setViewAsError] = useState("");

  const [filterProject, setFilterProject] = useState("All");
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });

  const [currentPage, setCurrentPage] = useState("home");
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [preselectProject, setPreselectProject] = useState(""); // chosen on home
  const [sessions, setSessions] = useState([]);
  const [sharedSessions, setSharedSessions] = useState([]);
  const [sessionsReady, setSessionsReady] = useState(false);
  const [tokenRetry, setTokenRetry] = useState(0); // stale-token self-heal counter

  const [shareModal, setShareModal] = useState(null);
  const [sharePid, setSharePid] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareToast, setShareToast] = useState(null);
  const [shareError, setShareError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [addPartnerSessionId, setAddPartnerSessionId] = useState(null);
  const [addPartnerValue, setAddPartnerValue] = useState("");
  const [addPartnerSaving, setAddPartnerSaving] = useState(false);
  const [addPartnerError, setAddPartnerError] = useState("");
  const [shareModalError, setShareModalError] = useState("");
  const [deleteModal, setDeleteModal] = useState(null); // session pending delete
  const [deleting, setDeleting] = useState(false);
  // Theme — "dark" (default) or "light". Persisted in localStorage and
  // applied as data-theme on <html> so CSS variables resolve correctly.
  const [theme, setTheme] = useState(() =>
    (typeof localStorage !== "undefined" && localStorage.getItem("bdl-theme")) || "dark"
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("bdl-theme", theme); } catch (e) { /* ignore */ }
  }, [theme]);
  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  // Auth state — look up PID from Notion on login
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setAuthLoading(true);
        try {
          const getUserPID = httpsCallable(functions, "getUserPID");
          const result = await getUserPID({ email: currentUser.email });
          // getUserPID stamps pid/admin custom claims on our auth user; force a
          // token refresh so those claims are present before we start querying
          // Firestore (security rules read them from the token).
          try { await currentUser.getIdToken(true); } catch (e) { /* ignore */ }
          const identity = {
            pid: result.data.pid,
            name: result.data.name,
            projects: visibleProjects(result.data.projects),
            batchUploader: result.data.batchUploader === true,
          };
          setRealIdentity(identity);
          setIsAdmin(result.data.admin === true);
          setViewAs(null);
          setPid(identity.pid);
          setUserName(identity.name);
          setProjects(identity.projects);
          setBatchUploader(identity.batchUploader);
          setUser(currentUser);
          setCurrentPage("home");
        } catch (err) {
          setAccessError(err.message || "Access denied.");
          await signOut(auth);
        }
        setAuthLoading(false);
      } else {
        setUser(null);
        setPid(null);
        setUserName("");
        setAuthLoading(false);
        // Note: accessError is intentionally not cleared here so the user sees it.
        // It gets cleared when they click "Try Again".
      }
    });
    return () => unsubscribeAuth();
  }, []);


  // Firestore subscription — sessions across ALL projects via collectionGroup
  // queries, not limited to the user's assigned project list. This ensures a
  // session shared with the user (e.g. a partner invite in a project they're
  // not assigned to) always loads. Each snapshot returns the full result set,
  // so we rebuild state directly rather than merging per-project listeners.
  useEffect(() => {
    if (!pid) { setSessionsReady(true); return; }

    const unsubscribers = [];

    // A stale ID token (minted before the pid claim was set) yields
    // permission-denied under the ownership rules. Force a one-time token
    // refresh and re-subscribe so the user self-heals instead of seeing an
    // empty dashboard. Bounded to avoid a loop if denial is genuine.
    const onListenerError = (label) => (err) => {
      console.error(label, err);
      setSessionsReady(true);
      if (err?.code === "permission-denied" && tokenRetry < 2 && auth.currentUser) {
        auth.currentUser.getIdToken(true)
          .then(() => setTokenRetry((n) => n + 1))
          .catch(() => {});
      }
    };

    // Own sessions — created by this user, in any project.
    const q1 = query(collectionGroup(db, "sessions"), where("creatorPid", "==", pid));
    unsubscribers.push(onSnapshot(q1,
      (snap) => {
        setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setSessionsReady(true);
      },
      onListenerError("sessions error:")
    ));

    // Shared sessions — shared with this user, in any project.
    const q2 = query(collectionGroup(db, "sessions"), where("sharedWith", "array-contains", pid));
    unsubscribers.push(onSnapshot(q2,
      (snap) => {
        setSharedSessions(snap.docs.map(d => ({ id: d.id, ...d.data(), isShared: true })));
      },
      onListenerError("sharedSessions error:")
    ));

    return () => { unsubscribers.forEach(u => u()); setSessionsReady(false); };
  }, [pid, tokenRetry]);


  // ── Admin "View as user" ──────────────────────────────────────────────
  // Resolve the target (PID or email), then swap the effective identity so
  // the dashboard/subscription render as that user. Read-only is enforced
  // everywhere via the `viewAs` flag.
  const enterViewAs = async () => {
    const raw = viewAsInput.trim();
    if (!raw || viewAsLoading) return;
    setViewAsLoading(true);
    setViewAsError("");
    try {
      let target;
      if (raw.includes("@")) {
        const getUserPID = httpsCallable(functions, "getUserPID");
        const { data } = await getUserPID({ email: raw });
        target = { pid: data.pid, name: data.name, projects: data.projects || [] };
      } else {
        const validatePID = httpsCallable(functions, "validatePID");
        const { data } = await validatePID({ pid: raw });
        target = { pid: data.pid, name: data.name, projects: data.projects || [] };
      }
      if (realIdentity && target.pid === realIdentity.pid) {
        setViewAsError("That's your own account.");
        return;
      }
      setViewAs(target);
      setPid(target.pid);
      setUserName(target.name || `PID ${target.pid}`);
      // Carry the target's Notion project assignments so project-gated sections
      // (e.g. Outdoor Guide) show in view-as. Empty for non-admin lookups; the
      // dashboard still derives its own project filter from the sessions.
      setProjects(target.projects || []);
      setBatchUploader(false);
      setActiveSessionId(null);
      setFilterProject("All");
      setCurrentPage("dashboard");
      setViewAsInput("");
    } catch (err) {
      setViewAsError(err.message || "User not found.");
    } finally {
      setViewAsLoading(false);
    }
  };

  const exitViewAs = () => {
    if (!realIdentity) return;
    setViewAs(null);
    setPid(realIdentity.pid);
    setUserName(realIdentity.name);
    setProjects(realIdentity.projects);
    setBatchUploader(realIdentity.batchUploader);
    setActiveSessionId(null);
    setFilterProject("All");
    setCurrentPage("dashboard");
  };

  const handleCreateSession = async (sessionDoc) => {
    if (viewAs) return; // read-only while impersonating
    const { project, sessionId } = sessionDoc;
    const docRef = doc(db, "projects", project, "sessions", sessionId);
    await setDoc(docRef, { ...sessionDoc, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    setActiveSessionId(sessionId);
    setCurrentPage("workspace");
  };

  const handleSubmitForQA = async () => {
    if (viewAs) return; // read-only while impersonating
    if (!activeSession) return;
    const { project, sessionId, title, creatorPid } = activeSession;
    const docRef = doc(db, "projects", project, "sessions", sessionId);
    try {
      // Submit to the DMS FIRST. Only if it succeeds do we mark the session
      // as submitted — otherwise a DMS failure would leave the row showing
      // "Waiting for QA" with no task actually created.
      const submitToTBD = httpsCallable(functions, "submitToTBD");
      // The server reads the session from Firestore itself (we only name it)
      // and allocates the submission number atomically.
      const result = await submitToTBD({ project, sessionId });

      // Rejected folder, but none of the reviewed files changed — reopening
      // would re-present the same rejected files, so warn instead of flipping
      // the status to "Waiting for QA" (which the sync would just revert).
      if (result.data?.rejectedNoChange) {
        setSubmitError("No reviewed files changed since the rejection, so re-submitting won't clear it. Update the flagged video, expert/student audio, or recipe — the Video (Zoom) slot isn't reviewed by QA.");
        return;
      }

      const updates = { status: "Waiting for QA", updatedAt: serverTimestamp() };
      if (result.data?.taskId) {
        updates.tbdTaskId = result.data.taskId;
        if (Number.isInteger(result.data.submissionNumber)) {
          updates.submissionNumber = result.data.submissionNumber;
        }
      }
      await updateDoc(docRef, updates);

      // Create the Notion video entry the first time only — on a re-submit
      // after reopening, the entry already exists, so skip to avoid duplicates.
      if (!activeSession.notionPageId) {
        try {
          const createNotionVideoEntry = httpsCallable(functions, "createNotionVideoEntry");
          const nres = await createNotionVideoEntry({ sessionId, title, project, creatorPid });
          if (nres.data?.notionPageId) {
            await updateDoc(docRef, { notionPageId: nres.data.notionPageId });
          }
        } catch (err) {
          console.warn("Notion entry failed:", err.message);
        }
      }
      setCurrentPage("dashboard");
    } catch (err) {
      // DMS submission failed — leave the session as Draft (not misleadingly
      // "submitted") and surface the error so the user can retry.
      setSubmitError(err.message || "Unknown error submitting to DMS.");
    }
  };

  // Hard-deletes a session doc. Storage files are cleaned up server-side by
  // the onSessionDeleted Cloud Function. Only the creator can delete, and
  // only while status === "Draft" (gated in SubmissionTable + workspace).
  // If the user was inside the workspace for this session, navigate them
  // back to the dashboard since the workspace would otherwise show
  // "Loading session…" once the Firestore listener removes the row.
  const handleDeleteSession = async (session) => {
    if (viewAs) return; // read-only while impersonating
    setDeleting(true);
    try {
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await deleteDoc(docRef);
      if (activeSessionId === session.sessionId) {
        setActiveSessionId(null);
        setCurrentPage("dashboard");
      }
      setDeleteModal(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleAcceptInvite = async (session) => {
    if (viewAs) return; // read-only while impersonating
    const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
    await updateDoc(docRef, {
      partnerPids: arrayUnion(pid),
      pendingPartnerPids: arrayRemove(pid),
      updatedAt: serverTimestamp(),
    });
  };

  const handleDenyInvite = async (session) => {
    if (viewAs) return; // read-only while impersonating
    const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
    await updateDoc(docRef, {
      pendingPartnerPids: arrayRemove(pid),
      sharedWith: arrayRemove(pid),
      updatedAt: serverTimestamp(),
    });
  };

  const handleAddPartnerInline = async (session) => {
    if (viewAs) return; // read-only while impersonating
    const trimmed = addPartnerValue.trim();
    if (!trimmed || addPartnerSaving) return;
    setAddPartnerSaving(true);
    setAddPartnerError("");
    try {
      const validatePID = httpsCallable(functions, "validatePID");
      const { data } = await validatePID({ pid: trimmed });
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      // Auto-accepted — partner sees it immediately (no pending invite).
      await updateDoc(docRef, {
        partnerPids: arrayUnion(data.pid),
        sharedWith: arrayUnion(data.pid),
        updatedAt: serverTimestamp(),
      });
      setAddPartnerSessionId(null);
      setAddPartnerValue("");
      setShareToast({ name: `${data.name} (PID ${data.pid})`, folder: session.title });
    } catch (err) {
      setAddPartnerError(err.message || "PID not found.");
    }
    setAddPartnerSaving(false);
  };

  const handleOpenSession = (session) => {
    setActiveSessionId(session.sessionId);
    setCurrentPage("workspace");
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    // Force the Google account chooser to appear every time, even if the
    // browser is already signed into one account. Lets users pick a
    // different identity (or switch teammates) without clearing cookies.
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      alert("Login Error: " + error.message);
    }
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleShareSubmit = async () => {
    if (viewAs) return; // read-only while impersonating
    const trimmedPid = sharePid.trim();
    if (!trimmedPid) return;
    setShareLoading(true);
    setShareModalError("");
    try {
      const validatePID = httpsCallable(functions, "validatePID");
      const { data } = await validatePID({ pid: trimmedPid });
      const session = shareModal;
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      // Auto-accepted — partner sees it immediately (no pending invite).
      await updateDoc(docRef, {
        partnerPids: arrayUnion(data.pid),
        sharedWith: arrayUnion(data.pid),
        updatedAt: serverTimestamp(),
      });
      setShareModal(null);
      setSharePid("");
      setShareToast({ name: `${data.name} (PID ${data.pid})`, folder: session.title });
    } catch (err) {
      setShareModalError(err.message || "PID not found.");
    }
    setShareLoading(false);
  };

  // Loading while looking up PID
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: C.text3, fontSize: "14px" }}>Signing in…</div>
      </div>
    );
  }

  // Access denied
  if (accessError) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "fixed", inset: 0, backgroundImage: "radial-gradient(var(--border-2) 1.5px, transparent 1.5px)", backgroundSize: "28px 28px", pointerEvents: "none", zIndex: 0 }} />
        <div className="orb-1" style={{ position: "fixed", width: "560px", height: "560px", background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 68%)", top: "-140px", left: "-140px", pointerEvents: "none", zIndex: 0 }} />
        <div className="orb-2" style={{ position: "fixed", width: "480px", height: "480px", background: "radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 68%)", bottom: "-100px", right: "-100px", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ textAlign: "center", maxWidth: "420px", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: "36px", marginBottom: "16px" }}>🔒</div>
          <div style={{ fontSize: "22px", fontWeight: "700", color: C.text1, marginBottom: "12px" }}>
            Access Not Found
          </div>
          <div style={{ fontSize: "16px", color: C.text2, lineHeight: "1.7", marginBottom: "10px" }}>
            We couldn't find your email in our system. Please double-check that you signed in with the correct Google account.
          </div>
          <div style={{ fontSize: "15px", color: C.text3, lineHeight: "1.7", marginBottom: "28px" }}>
            If you believe this is a mistake, reach out to us at <a href="mailto:outreach@secondorbit.ai" style={{ color: "#818cf8" }}>outreach@secondorbit.ai</a> and we'll get you set up.
          </div>
          <button
            onClick={() => setAccessError("")}
            style={{ padding: "10px 28px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}
          >
            Try a Different Account
          </button>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage onLogin={handleGoogleLogin} />;

  const pendingInvites = sharedSessions.filter(s =>
    s.creatorPid !== pid && s.pendingPartnerPids?.includes(pid)
  );
  const allSessions = [
    ...sessions,
    ...sharedSessions.filter(s => {
      if (s.creatorPid === pid) return false;
      const accepted = s.partnerPids ?? (s.partnerPid ? [s.partnerPid] : []);
      return accepted.includes(pid);
    }),
  ];

  const activeSession = activeSessionId
    ? (allSessions.find(s => s.sessionId === activeSessionId) ?? null)
    : null;

  // Outdoor Guide is a separate uploader section, shown only to creators who
  // have the "Outdoor Guide" project assigned in Notion.
  const showOutdoor = projects.includes("Outdoor Guide");

  // Projects closed to NEW submissions — hidden from the creation picker and
  // the session creator, but kept in the dashboard filter so existing sessions
  // remain viewable/filterable. (EH/Cooking paused, matched case-insensitively.)
  const CLOSED_PROJECTS = new Set(["cooking"]);
  const creatableProjects = projects.filter(
    p => !CLOSED_PROJECTS.has(String(p).trim().toLowerCase())
  );

  // "Waiting for QA" is the canonical post-submit status (set on submit and
  // kept by the Notion sync). "Submitted for QA" is a legacy/transient value
  // some older docs may still carry, so count both.
  const isWaitingForQA = (s) =>
    s.status === "Waiting for QA" || s.status === "Submitted for QA";
  const stats = {
    // Total excludes drafts — an unsubmitted draft isn't a real session yet.
    total:     allSessions.filter(s => s.status !== "Draft").length,
    draft:     allSessions.filter(s => s.status === "Draft").length,
    submitted: allSessions.filter(isWaitingForQA).length,
    inProgress: allSessions.filter(s => s.status === "In Progress").length,
    approved:  allSessions.filter(s => s.status === "Approved").length,
    rejected:  allSessions.filter(s => s.status === "Rejected").length,
    // Payment: paid videos vs approved-but-not-yet-paid (the payable ones
    // still awaiting payment). `paid` is set by the TBD payments sync.
    paid:      allSessions.filter(s => s.paid === true).length,
    notPaid:   allSessions.filter(s => s.status === "Approved" && s.paid !== true).length,
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      {/* Background */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: "radial-gradient(var(--border-2) 1.5px, transparent 1.5px)", backgroundSize: "28px 28px", pointerEvents: "none", zIndex: 0 }} />
      <div className="orb-1" style={{ position: "fixed", width: "560px", height: "560px", background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 68%)", top: "-140px", left: "-140px", pointerEvents: "none", zIndex: 0 }} />
      <div className="orb-2" style={{ position: "fixed", width: "480px", height: "480px", background: "radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 68%)", bottom: "-100px", right: "-100px", pointerEvents: "none", zIndex: 0 }} />
      <div className="orb-3" style={{ position: "fixed", width: "320px", height: "320px", background: "radial-gradient(circle, rgba(96,165,250,0.12) 0%, transparent 68%)", top: "10%", right: "8%", pointerEvents: "none", zIndex: 0 }} />

      <Sidebar user={user} userName={userName} pid={pid} currentPage={currentPage} onNavigate={setCurrentPage} theme={theme} onToggleTheme={toggleTheme} showOutdoor={showOutdoor} />

      <main className="main-content" style={{ marginLeft: "248px", flex: 1, padding: "44px 48px 60px", position: "relative", zIndex: 1, maxWidth: "calc(100vw - 248px)" }}>

      {/* ── Admin: View-as banner / control ── */}
      {viewAs ? (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
          padding: "12px 18px", marginBottom: "24px",
          background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)",
          borderRadius: "12px",
        }}>
          <span style={{ fontSize: "13px", color: "#f59e0b", fontWeight: "600" }}>
            👁 Viewing as {userName} (PID {pid}) — read-only. You're seeing this user's dashboard exactly as they do.
          </span>
          <button
            onClick={exitViewAs}
            style={{ padding: "7px 16px", borderRadius: "8px", background: "rgba(245,158,11,0.18)", border: "1px solid rgba(245,158,11,0.4)", color: "#f59e0b", fontSize: "13px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
          >
            Exit view-as
          </button>
        </div>
      ) : isAdmin && (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
          padding: "12px 18px", marginBottom: "24px",
          background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: "12px",
        }}>
          <span style={{ fontSize: "12px", fontWeight: "700", color: "#818cf8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Admin · View as user
          </span>
          <input
            type="text"
            placeholder="PID or email"
            value={viewAsInput}
            onChange={(e) => { setViewAsInput(e.target.value); setViewAsError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") enterViewAs(); }}
            disabled={viewAsLoading}
            style={{ fontSize: "13px", background: "var(--surface-2)", border: `1px solid ${viewAsError ? "rgba(248,113,113,0.45)" : "var(--border-2)"}`, borderRadius: "8px", padding: "7px 12px", color: C.text1, outline: "none", width: "200px", fontFamily: "inherit" }}
          />
          <button
            onClick={enterViewAs}
            disabled={!viewAsInput.trim() || viewAsLoading}
            style={{ padding: "7px 16px", borderRadius: "8px", background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.4)", color: "#818cf8", fontSize: "13px", fontWeight: "600", cursor: viewAsInput.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}
          >
            {viewAsLoading ? "Loading…" : "View →"}
          </button>
          {viewAsError && <span style={{ fontSize: "12px", color: "#f87171" }}>{viewAsError}</span>}
        </div>
      )}

      <div key={currentPage} className="fade-in-up" style={{ height: "100%" }}>

        {/* Home */}
        {currentPage === "home" && (() => {
          const hour = new Date().getHours();
          const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
          const homeCards = [
            {
              icon: "📊",
              num: "01",
              title: "Track Progress",
              desc: "Monitor your submission status, check approval progress, and see a breakdown of your pending and paid earnings.",
              btnLabel: "Open Dashboard",
              btnBg: "rgba(16,185,129,0.12)",
              btnColor: C.success,
              btnBorder: `1px solid rgba(16,185,129,0.25)`,
              accentColor: "rgba(16,185,129,0.12)",
              accentBorder: "rgba(16,185,129,0.22)",
              iconColor: C.success,
              onClick: () => setCurrentPage("dashboard"),
            },
            {
              icon: "🤝",
              num: "02",
              title: "Invite a Partner",
              desc: pendingInvites.length > 0
                ? `You have ${pendingInvites.length} pending session invite${pendingInvites.length > 1 ? "s" : ""} — head to the dashboard to accept or deny.`
                : "Collaborating with someone? Add their PID when creating a session or from the dashboard — they'll get an invite to accept.",
              btnLabel: pendingInvites.length > 0 ? "View Invites" : "Go to Dashboard",
              btnBg: "rgba(139,92,246,0.12)",
              btnColor: C.violet,
              btnBorder: `1px solid rgba(139,92,246,0.22)`,
              accentColor: "rgba(139,92,246,0.12)",
              accentBorder: "rgba(139,92,246,0.22)",
              iconColor: C.violet,
              badge: pendingInvites.length || null,
              onClick: () => setCurrentPage("dashboard"),
            },
            {
              icon: "⬆️",
              num: "03",
              title: "Batch Upload",
              desc: "Already have all your files ready? Drop everything at once — slots are auto-labeled by filename and you can adjust before uploading.",
              btnLabel: "Batch Upload",
              btnBg: "rgba(245,158,11,0.12)",
              btnColor: C.warning,
              btnBorder: `1px solid rgba(245,158,11,0.22)`,
              accentColor: "rgba(245,158,11,0.12)",
              accentBorder: "rgba(245,158,11,0.22)",
              iconColor: C.warning,
              onClick: () => setCurrentPage("batch"),
            },
          ];

          return (
            <div>
              {/* Greeting */}
              <div style={{ marginBottom: "44px" }}>
                <h1 style={{ fontSize: "30px", fontWeight: "700", color: C.text1, letterSpacing: "-0.03em", marginBottom: "8px" }}>
                  {greeting}, {userName}
                </h1>
                <p style={{ fontSize: "15px", color: C.text3, lineHeight: "1.6" }}>
                  {allSessions.length > 0
                    ? "Here's a snapshot of your activity. What would you like to do next?"
                    : "Welcome to the Second Orbit Contributor Portal. Follow the steps below to get started."}
                </p>
                {allSessions.length > 0 && (
                  <div style={{ display: "flex", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "12px", color: C.blue, background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)", padding: "4px 12px", borderRadius: "20px", fontWeight: "500" }}>
                      {allSessions.length} session{allSessions.length !== 1 ? "s" : ""}
                    </span>
                    <span style={{ fontSize: "12px", color: C.success, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", padding: "4px 12px", borderRadius: "20px", fontWeight: "500" }}>
                      {allSessions.filter(isWaitingForQA).length} waiting for QA
                    </span>
                    {allSessions.filter(s => s.status === "Draft").length > 0 && (
                      <span style={{ fontSize: "12px", color: C.warning, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", padding: "4px 12px", borderRadius: "20px", fontWeight: "500" }}>
                        {allSessions.filter(s => s.status === "Draft").length} draft
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Project picker — primary CTA */}
              <div style={{ marginBottom: "40px" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "700", color: C.text1, letterSpacing: "-0.02em", marginBottom: "6px" }}>
                  Select the project you're working on
                </h2>
                <p style={{ fontSize: "13px", color: C.text3, marginBottom: "20px" }}>
                  Choose a project to jump into the right workflow.
                </p>
                {creatableProjects.length === 0 ? (
                  <div style={{ fontSize: "13px", color: C.text3, padding: "24px", border: "1px dashed var(--border-2)", borderRadius: "12px" }}>
                    No projects are assigned to your account yet. Contact your admin to get access.
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "16px" }}>
                    {creatableProjects.map((p) => {
                      const isOG = p === "Outdoor Guide";
                      return (
                        <button
                          key={p}
                          className="home-card"
                          onClick={() => {
                            if (isOG) {
                              setCurrentPage("outdoor");
                            } else {
                              setPreselectProject(p);
                              setFilterProject(p);
                              setCurrentPage("upload");
                            }
                          }}
                          style={{
                            textAlign: "left", background: "var(--card)",
                            border: "1px solid var(--border)", borderRadius: "16px",
                            padding: "22px", cursor: "pointer", fontFamily: "inherit",
                            display: "flex", flexDirection: "column", gap: "10px",
                          }}
                        >
                          <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: isOG ? "rgba(16,185,129,0.12)" : "rgba(99,102,241,0.12)", border: `1px solid ${isOG ? "rgba(16,185,129,0.25)" : "rgba(99,102,241,0.25)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>
                            {projectIcon(p)}
                          </div>
                          <div style={{ fontSize: "16px", fontWeight: "700", color: C.text1, letterSpacing: "-0.01em" }}>{p}</div>
                          <div style={{ fontSize: "12px", color: C.text3, lineHeight: "1.5", flex: 1 }}>
                            {isOG ? "Outdoor recordings — location → task → clip" : "Recording sessions — upload & track"}
                          </div>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: isOG ? C.success : "#818cf8" }}>
                            {isOG ? "Open Outdoor Guide →" : "Work on this project →"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Quick actions */}
              <div style={{ fontSize: "12px", fontWeight: "700", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "14px" }}>
                Quick actions
              </div>
              <div className="home-cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "18px" }}>
                {homeCards.map((card) => (
                  <div
                    key={card.num}
                    className="home-card"
                    style={{
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: "18px",
                      padding: "28px",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Top accent line */}
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, ${card.iconColor}55, transparent)` }} />

                    {/* Number badge */}
                    <div style={{ position: "absolute", top: "20px", right: "20px", fontSize: "11px", fontWeight: "700", color: "var(--border)", letterSpacing: "0.06em" }}>
                      {card.num}
                    </div>

                    {/* Notification badge */}
                    {card.badge && (
                      <div style={{ position: "absolute", top: "16px", left: "16px", minWidth: "20px", height: "20px", borderRadius: "999px", background: "#c084fc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", color: "#fff", padding: "0 5px" }}>
                        {card.badge}
                      </div>
                    )}

                    {/* Icon */}
                    <div style={{
                      width: "48px", height: "48px", borderRadius: "14px",
                      background: card.accentColor,
                      border: `1px solid ${card.accentBorder}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "22px", marginBottom: "18px",
                    }}>
                      {card.icon}
                    </div>

                    <div style={{ fontSize: "16px", fontWeight: "700", color: C.text1, marginBottom: "8px", letterSpacing: "-0.02em" }}>
                      {card.title}
                    </div>
                    <div style={{ fontSize: "14px", color: C.text3, lineHeight: "1.7", flex: 1, marginBottom: "22px" }}>
                      {card.desc}
                    </div>

                    <button
                      className="home-action-btn"
                      onClick={card.onClick}
                      style={{
                        padding: "9px 16px",
                        background: card.btnBg,
                        color: card.btnColor,
                        border: card.btnBorder,
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        alignSelf: "flex-start",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {card.btnLabel} →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Read-only notice when an admin lands on a create page while viewing-as */}
        {viewAs && (currentPage === "upload" || currentPage === "batch") && (
          <div style={{ textAlign: "center", padding: "72px 24px", color: C.text3 }}>
            <div style={{ fontSize: "40px", marginBottom: "16px", opacity: 0.5 }}>👁</div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: C.text1, marginBottom: "8px" }}>Read-only while viewing as {userName}</div>
            <div style={{ fontSize: "14px", lineHeight: "1.6", marginBottom: "24px" }}>
              Creating and uploading are disabled in view-as mode.<br />Use the Dashboard to inspect this user's sessions.
            </div>
            <button
              onClick={() => setCurrentPage("dashboard")}
              style={{ padding: "10px 24px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Session Management */}
        {currentPage === "upload" && !viewAs && (
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: "700", color: C.text1, letterSpacing: "-0.03em", marginBottom: "6px" }}>Session Management</h1>
            <p style={{ fontSize: "15px", color: C.text3, marginBottom: "16px" }}>Create a named workspace first, then upload files slot by slot — or use the individual slot cards inside each session.</p>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.18)", borderRadius: "10px", marginBottom: "32px" }}>
              <span style={{ fontSize: "14px" }}>⚡</span>
              <span style={{ fontSize: "13px", color: C.text3 }}>Already have all your files ready?</span>
              <button
                onClick={() => setCurrentPage("batch")}
                style={{ fontSize: "13px", fontWeight: "600", color: C.warning, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}
              >
                Try Batch Upload →
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px", alignItems: "start" }}>
              {/* Left: create new session */}
              <SessionCreator
                projects={creatableProjects}
                pid={pid}
                userName={userName}
                userEmail={user.email}
                onCreate={handleCreateSession}
                // Leave the submission number blank so the server allocates it
                // atomically at submit time. Pre-filling a client-computed
                // number is what let multiple sessions collide on one task ID.
                getDefaultSubmissionNumber={() => ""}
                onOpenOutdoor={() => setCurrentPage("outdoor")}
                initialProject={preselectProject}
              />
              {/* Right: pick up where you left off */}
              <div>
                <div style={{ marginBottom: "14px" }}>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: C.text1, letterSpacing: "-0.01em", marginBottom: "3px" }}>
                    Pick up where you left off
                  </div>
                  <div style={{ fontSize: "13px", color: C.text3 }}>Your in-progress sessions — click Continue to open the workspace.</div>
                </div>
                {allSessions.filter(s => s.status === "Draft" && (s.creatorPid === pid || s.partnerPids?.includes(pid))).length === 0 ? (
                  <div style={{ fontSize: "13px", color: C.text3, padding: "32px 24px", border: "1px dashed var(--border)", borderRadius: "12px", textAlign: "center", lineHeight: "1.7" }}>
                    No sessions in progress yet.<br />
                    <span style={{ fontSize: "12px", color: "rgba(140,140,160,0.6)" }}>Create one on the left to get started.</span>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {allSessions
                      .filter(s => s.status === "Draft" && (s.creatorPid === pid || s.partnerPids?.includes(pid)))
                      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                      .map(s => {
                        const filled = REQUIRED_SLOTS.filter(slot => s.slots?.[slot]?.activeFile).length;
                        return (
                          <div key={s.sessionId} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px 18px", display: "flex", alignItems: "center", gap: "14px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: "14px", fontWeight: "600", color: C.text1, marginBottom: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</div>
                              {(() => {
                                const c2 = s.partnerPids?.[0] || s.secondCreatorPid || s.creatorPid;
                                const num = s.submissionNumber != null ? s.submissionNumber : "auto";
                                return (
                                  <div style={{ fontSize: "11px", color: C.text3, fontFamily: "monospace" }}>
                                    {`${projectCode(s.project)}_P${stripPidPrefix(s.creatorPid)}_P${stripPidPrefix(c2)}_${num}`}
                                  </div>
                                );
                              })()}
                              {s.creatorPid !== pid && (
                                <span style={{ fontSize: "11px", color: "#a78bfa", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", padding: "1px 7px", borderRadius: "4px", display: "inline-block", marginTop: "3px" }}>
                                  Shared by PID {s.creatorPid}
                                </span>
                              )}
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                                <div style={{ flex: 1, height: "4px", background: "var(--border-subtle)", borderRadius: "999px", overflow: "hidden", maxWidth: "80px" }}>
                                  <div style={{ width: `${(filled / REQUIRED_SLOTS.length) * 100}%`, height: "100%", background: filled === REQUIRED_SLOTS.length ? "#10b981" : C.primary, borderRadius: "999px" }} />
                                </div>
                                <span style={{ fontSize: "11px", color: C.text3, fontWeight: "600" }}>{filled}/{REQUIRED_SLOTS.length}</span>
                                {((s.partnerPids?.length > 0) || (s.pendingPartnerPids?.length > 0)) && (
                                  <span style={{ fontSize: "11px", color: "#a78bfa" }}>
                                    {[
                                      ...(s.partnerPids || []).map(p => `PID ${p}`),
                                      ...(s.pendingPartnerPids || []).map(p => `PID ${p} (invited)`),
                                    ].join(", ")}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Add partner controls — creator only */}
                            {s.creatorPid === pid && addPartnerSessionId !== s.sessionId && (
                              <button
                                onClick={() => { setAddPartnerSessionId(s.sessionId); setAddPartnerValue(""); }}
                                style={{ padding: "5px 10px", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: "7px", color: "#a78bfa", fontSize: "12px", fontWeight: "600", cursor: "pointer", flexShrink: 0, fontFamily: "inherit", whiteSpace: "nowrap" }}
                              >
                                + Partner
                              </button>
                            )}
                            {s.creatorPid === pid && addPartnerSessionId === s.sessionId && (
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px", flexShrink: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                  <input
                                    autoFocus
                                    type="text"
                                    placeholder="PID"
                                    value={addPartnerValue}
                                    onChange={e => { setAddPartnerValue(e.target.value); setAddPartnerError(""); }}
                                    onKeyDown={e => { if (e.key === "Enter") handleAddPartnerInline(s); if (e.key === "Escape") { setAddPartnerSessionId(null); setAddPartnerValue(""); setAddPartnerError(""); } }}
                                    style={{ fontSize: "12px", background: "var(--border-subtle)", border: `1px solid ${addPartnerError ? "rgba(248,113,113,0.4)" : "rgba(167,139,250,0.3)"}`, borderRadius: "6px", padding: "4px 8px", color: "#fff", outline: "none", width: "70px", fontFamily: "inherit" }}
                                  />
                                  <button
                                    onClick={() => handleAddPartnerInline(s)}
                                    disabled={!addPartnerValue.trim() || addPartnerSaving}
                                    style={{ padding: "4px 8px", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: "6px", color: "#a78bfa", fontSize: "12px", fontWeight: "600", cursor: addPartnerValue.trim() ? "pointer" : "not-allowed", fontFamily: "inherit", whiteSpace: "nowrap" }}
                                  >
                                    {addPartnerSaving ? "…" : "Add →"}
                                  </button>
                                  <button
                                    onClick={() => { setAddPartnerSessionId(null); setAddPartnerValue(""); setAddPartnerError(""); }}
                                    style={{ background: "none", border: "none", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: "16px", padding: "0", lineHeight: 1, fontFamily: "inherit" }}
                                  >
                                    ×
                                  </button>
                                </div>
                                {addPartnerError && <div style={{ fontSize: "11px", color: "#f87171" }}>{addPartnerError}</div>}
                              </div>
                            )}
                            <button
                              onClick={() => handleOpenSession(s)}
                              style={{ padding: "7px 14px", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: "7px", color: "#818cf8", fontSize: "13px", fontWeight: "600", cursor: "pointer", flexShrink: 0, fontFamily: "inherit", whiteSpace: "nowrap" }}
                            >
                              Continue →
                            </button>
                          </div>
                        );
                      })
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Batch Upload */}
        {/* Batch Upload is rendered outside this keyed wrapper (below) so it
            stays mounted across navigation and in-flight uploads survive. */}

        {/* Session workspace */}
        {currentPage === "workspace" && (
          <SessionWorkspace
            session={activeSession}
            pid={pid}
            userName={userName}
            onSubmitForQA={handleSubmitForQA}
            onBack={() => setCurrentPage("dashboard")}
            onRequestDelete={(s) => setDeleteModal(s)}
            viewingAs={!!viewAs}
          />
        )}

        {/* Outdoor Guide */}
        {currentPage === "outdoor" && showOutdoor && (
          <OutdoorGuide pid={pid} userName={userName} readOnly={!!viewAs} />
        )}

        {/* Dashboard */}
        {currentPage === "dashboard" && (
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: 700, color: C.text1, letterSpacing: "-0.02em", lineHeight: 1.2, marginBottom: SP[2] }}>Dashboard</h1>
            <p style={{ fontSize: "14px", color: C.text2, marginBottom: SP[8] }}>Your sessions and progress at a glance</p>
            {!sessionsReady ? (
              <div style={{ textAlign: "center", padding: "80px 24px", color: C.text3, fontSize: "14px" }}>
                Loading…
              </div>
            ) : (
              <>
                {/* ── Pending invites ── */}
                {pendingInvites.length > 0 && (
                  <div style={{ marginBottom: "32px" }}>
                    <div style={{ fontSize: "11px", fontWeight: "600", color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "12px" }}>
                      🔔 Pending Invites ({pendingInvites.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {pendingInvites.map(s => (
                        <div key={s.sessionId} style={{
                          background: "var(--card)",
                          border: "1px solid rgba(245,158,11,0.22)",
                          borderRadius: "12px",
                          padding: "16px 20px",
                          display: "flex", alignItems: "center", gap: "16px",
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "14px", fontWeight: "600", color: C.text1, marginBottom: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {s.title}
                            </div>
                            <div style={{ fontSize: "12px", color: C.text3 }}>
                              {s.project} · Invited by {s.creatorName || `PID ${s.creatorPid}`}
                            </div>
                            <div style={{ fontSize: "11px", color: C.text3, fontFamily: "monospace", marginTop: "2px" }}>{s.sessionId}</div>
                          </div>
                          <div style={{ display: "flex", gap: SP[2], flexShrink: 0 }}>
                            <Btn variant="danger" size="sm" onClick={() => handleDenyInvite(s)}>Deny</Btn>
                            <Btn variant="success" size="sm" onClick={() => handleAcceptInvite(s)}>Accept</Btn>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {allSessions.length > 0 ? (
                  <>
                    <MetricsGrid stats={stats} />
                    {/* Payments widget — paid vs approved-but-unpaid videos */}
                    <div className="ui-card" style={{ display: "flex", alignItems: "center", gap: `${SP[2]} ${SP[5]}`, flexWrap: "wrap", padding: `${SP[3]} ${SP[4]}`, background: "var(--card)", border: "1px solid var(--border)", borderRadius: RAD.lg, marginBottom: SP[4] }}>
                      <span style={{ ...eyebrow }}>Payments</span>
                      <span style={{ display: "flex", alignItems: "center", gap: SP[2], fontSize: "13px", color: C.text2 }}>
                        <span aria-hidden="true" style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.success }} />
                        Paid <strong style={{ color: C.text1, fontSize: "14px" }}>{stats.paid}</strong>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: SP[2], fontSize: "13px", color: C.text2 }}>
                        <span aria-hidden="true" style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.warning }} />
                        Not yet paid <strong style={{ color: C.text1, fontSize: "14px" }}>{stats.notPaid}</strong>
                        <span style={{ fontSize: "12px", color: C.text3 }}>(approved, awaiting payment)</span>
                      </span>
                      {stats.paid + stats.notPaid > 0 && (
                        <span style={{ flex: 1, minWidth: "120px", height: "6px", background: "var(--border-subtle)", borderRadius: "999px", overflow: "hidden", maxWidth: "320px" }}>
                          <span style={{ display: "block", height: "100%", width: `${Math.round((stats.paid / (stats.paid + stats.notPaid)) * 100)}%`, background: C.success, borderRadius: "999px" }} />
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: "13px", color: C.text2, margin: `0 0 ${SP[4]}` }}>
                      Click <strong style={{ color: C.text1, fontWeight: 600 }}>Open</strong> to enter a session workspace, or <strong style={{ color: C.text1, fontWeight: 600 }}>Share</strong> to add a partner.
                    </p>
                    <SubmissionTable
                      sessions={allSessions}
                      pendingInvites={pendingInvites}
                      projects={projects}
                      filterProject={filterProject}
                      onFilterChange={setFilterProject}
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      onOpen={handleOpenSession}
                      onShare={(s) => setShareModal(s)}
                      onDelete={(s) => setDeleteModal(s)}
                      onAccept={handleAcceptInvite}
                      onDeny={handleDenyInvite}
                      creatorPid={pid}
                      readOnly={!!viewAs}
                    />
                  </>
                ) : pendingInvites.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "80px 24px" }}>
                    <div style={{ fontSize: "52px", marginBottom: "20px", opacity: 0.5 }}>📭</div>
                    <div style={{ fontSize: "20px", fontWeight: "700", color: C.text1, marginBottom: "10px" }}>No sessions yet</div>
                    <div style={{ fontSize: "14px", color: C.text3, marginBottom: "28px", lineHeight: "1.6" }}>
                      Create your first session to get started.
                    </div>
                    <button
                      onClick={() => setCurrentPage("upload")}
                      style={{ padding: "12px 28px", background: C.primary, color: "#fff", border: "none", borderRadius: "9px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
                    >
                      Create your first session
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>

      {/* Batch Upload — kept mounted across navigation (display-toggled rather
          than conditionally rendered) so in-flight uploads aren't cancelled
          when the user switches pages mid-upload. */}
      <div style={{ display: currentPage === "batch" && !viewAs ? "block" : "none", height: "100%" }}>
        <h1 style={{ fontSize: "26px", fontWeight: "700", color: C.text1, letterSpacing: "-0.03em", marginBottom: "6px" }}>Batch Upload</h1>
        <p style={{ fontSize: "15px", color: C.text3, marginBottom: "36px" }}>Drop all your files at once, assign slots, then map them to a session in one go.</p>
        <BatchUpload
          sessions={allSessions}
          pid={pid}
          userName={userName}
          userEmail={user.email}
          projects={creatableProjects}
          batchUploader={batchUploader}
          onDone={(project, sessionId) => {
            setActiveSessionId(sessionId);
            setCurrentPage("workspace");
          }}
        />
      </div>
      </main>

      {/* Share modal */}
      {shareModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "24px" }}>
          <div style={{ background: C.card, border: `1px solid ${C.border2}`, borderRadius: "16px", padding: "32px", width: "100%", maxWidth: "400px" }}>
            <div style={{ fontSize: "16px", fontWeight: "700", color: C.text1, marginBottom: "6px" }}>Add Partner</div>
            <div style={{ fontSize: "13px", color: C.text3, marginBottom: "20px" }}>
              Add a partner to <span style={{ color: C.text2, fontWeight: "500" }}>"{shareModal.title}"</span> — they'll see it in their dashboard and can upload their own files.
            </div>
            <input
              type="text"
              placeholder="Enter partner PID (e.g. 168)"
              value={sharePid}
              onChange={(e) => { setSharePid(e.target.value); setShareModalError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleShareSubmit()}
              style={{ width: "100%", padding: "10px 14px", background: C.surface, border: `1px solid ${shareModalError ? "rgba(248,113,113,0.45)" : C.border2}`, borderRadius: "8px", color: C.text1, fontSize: "14px", boxSizing: "border-box", marginBottom: shareModalError ? "8px" : "16px", outline: "none", fontFamily: "inherit" }}
            />
            {shareModalError && (
              <div style={{ fontSize: "12px", color: "#f87171", marginBottom: "12px" }}>{shareModalError}</div>
            )}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => { setShareModal(null); setSharePid(""); setShareModalError(""); }} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: "8px", color: C.text2, fontSize: "13px", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleShareSubmit} disabled={shareLoading} style={{ padding: "9px 18px", background: C.primary, border: "none", borderRadius: "8px", color: "#fff", fontSize: "13px", fontWeight: "600", cursor: "pointer", opacity: shareLoading ? 0.7 : 1 }}>
                {shareLoading ? "Checking…" : "Add Partner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share success toast */}
      {shareToast && (
        <div style={{
          position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 200, pointerEvents: "none",
        }}>
          <div className="toast-center" onAnimationEnd={() => setShareToast(null)} style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            border: "1px solid rgba(167,139,250,0.35)",
            borderRadius: "18px",
            padding: "28px 36px",
            display: "flex", alignItems: "center", gap: "18px",
            boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(167,139,250,0.1)",
            minWidth: "340px",
          }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "14px", flexShrink: 0,
              background: "rgba(167,139,250,0.15)",
              border: "1px solid rgba(167,139,250,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "22px",
            }}>
              🔗
            </div>
            <div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: C.text1, marginBottom: "4px" }}>
                Partner Added!
              </div>
              <div style={{ fontSize: "13px", color: C.text3, lineHeight: "1.5" }}>
                <span style={{ color: "#a78bfa", fontWeight: "600" }}>{shareToast.name}</span> has been invited to <span style={{ color: C.text2, fontWeight: "500" }}>"{shareToast.folder}"</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share error toast */}
      {shareError && (
        <div style={{
          position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 200, pointerEvents: "none",
        }}>
          <div className="toast-center" onAnimationEnd={() => setShareError(null)} style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            border: "1px solid rgba(248,113,113,0.35)",
            borderRadius: "18px",
            padding: "28px 36px",
            display: "flex", alignItems: "center", gap: "18px",
            boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(248,113,113,0.1)",
            minWidth: "340px",
          }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "14px", flexShrink: 0,
              background: "rgba(248,113,113,0.15)",
              border: "1px solid rgba(248,113,113,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "22px",
            }}>
              ✕
            </div>
            <div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: C.text1, marginBottom: "4px" }}>
                Couldn't Share Folder
              </div>
              <div style={{ fontSize: "13px", color: C.text3, lineHeight: "1.5" }}>
                {shareError}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DMS submission error toast */}
      {submitError && (
        <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, pointerEvents: "none" }}>
          <div className="toast-center" onAnimationEnd={() => setSubmitError(null)} style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            border: "1px solid rgba(248,113,113,0.35)",
            borderRadius: "18px",
            padding: "28px 36px",
            display: "flex", alignItems: "center", gap: "18px",
            boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(248,113,113,0.1)",
            minWidth: "340px", maxWidth: "480px",
          }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "14px", flexShrink: 0, background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>
              ✕
            </div>
            <div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: C.text1, marginBottom: "4px" }}>
                DMS Submission Failed
              </div>
              <div style={{ fontSize: "13px", color: C.text3, lineHeight: "1.5" }}>
                {submitError}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete-session confirmation */}
      {deleteModal && (() => {
        const filledSlots = Object.values(deleteModal.slots || {}).filter(s => s?.activeFile).length;
        const partnerCount = (deleteModal.partnerPids?.length || 0) + (deleteModal.pendingPartnerPids?.length || 0);
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "24px" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border2}`, borderRadius: "16px", padding: "32px", width: "100%", maxWidth: "440px" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: C.text1, marginBottom: "6px" }}>Delete this session?</div>
              <div style={{ fontSize: "13px", color: C.text3, marginBottom: "16px", lineHeight: "1.5" }}>
                <span style={{ color: C.text2, fontWeight: "500" }}>"{deleteModal.title}"</span> will be permanently removed. This cannot be undone.
              </div>
              {(filledSlots > 0 || partnerCount > 0) && (
                <div style={{ background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.22)", borderRadius: "9px", padding: "12px 14px", marginBottom: "18px", fontSize: "12px", color: "#fca5a5", lineHeight: "1.7" }}>
                  <div style={{ fontWeight: "600", marginBottom: "4px", color: "#fda4af" }}>⚠ What will be lost:</div>
                  {filledSlots > 0 && <div>· {filledSlots} uploaded file{filledSlots !== 1 ? "s" : ""}</div>}
                  {partnerCount > 0 && <div>· {partnerCount} partner{partnerCount !== 1 ? "s" : ""} will lose access</div>}
                </div>
              )}
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button onClick={() => setDeleteModal(null)} disabled={deleting} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: "8px", color: C.text2, fontSize: "13px", cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
                <button onClick={() => handleDeleteSession(deleteModal)} disabled={deleting} style={{ padding: "9px 18px", background: "var(--danger-btn-bg)", border: "1px solid var(--danger-btn-border)", borderRadius: "8px", color: "var(--danger-btn-color)", fontSize: "13px", fontWeight: "600", cursor: deleting ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? "Deleting…" : "Delete session"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Mobile sign-out confirmation */}
      {showSignOutConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 200, padding: "0 0 env(safe-area-inset-bottom, 0px)" }}>
          <div className="fade-in-up" style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "22px 22px 0 0", padding: "32px 28px 36px", width: "100%", maxWidth: "480px" }}>
            {/* Handle bar */}
            <div style={{ width: "36px", height: "4px", borderRadius: "2px", background: "var(--border-2)", margin: "0 auto 28px" }} />

            {/* Avatar */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "24px" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", fontWeight: "700", color: "#fff", marginBottom: "12px" }}>
                {(userName || user.email)[0].toUpperCase()}
              </div>
              <div style={{ fontSize: "16px", fontWeight: "600", color: C.text1 }}>{userName}</div>
              <div style={{ fontSize: "13px", color: C.text3, marginTop: "3px" }}>{user.email}</div>
            </div>

            <div style={{ height: "1px", background: "var(--border-subtle)", marginBottom: "24px" }} />

            <p style={{ fontSize: "14px", color: C.text3, textAlign: "center", marginBottom: "24px", lineHeight: "1.6" }}>
              Are you sure you want to sign out?
            </p>

            <button
              onClick={() => { setShowSignOutConfirm(false); signOut(auth); }}
              style={{ width: "100%", padding: "14px", background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: "12px", color: "#f87171", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginBottom: "10px" }}
            >
              Sign out
            </button>
            <button
              onClick={() => setShowSignOutConfirm(false)}
              style={{ width: "100%", padding: "14px", background: "transparent", border: "1px solid var(--border)", borderRadius: "12px", color: C.text2, fontSize: "15px", fontWeight: "500", cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mobile bottom navigation */}
      {user && pid && (
        <nav className="mobile-nav">
          {[
            { id: "home",      icon: "🏠", label: "Home" },
            { id: "upload",    icon: "📋", label: "Sessions" },
            { id: "batch",     icon: "⬆️", label: "Batch Upload" },
            { id: "dashboard", icon: "📊", label: "Dashboard" },
            ...(showOutdoor ? [{ id: "outdoor", icon: "🏞️", label: "Outdoor" }] : []),
          ].map((item) => (
            <button
              key={item.id}
              className={`mobile-nav-btn${currentPage === item.id ? " active" : ""}`}
              onClick={() => setCurrentPage(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
          <button
            className="mobile-nav-btn"
            onClick={() => setShowSignOutConfirm(true)}
            style={{ color: "rgba(255,255,255,0.3)" }}
          >
            <span style={{ width: "26px", height: "26px", borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "700", fontSize: "12px", flexShrink: 0 }}>
              {(userName || user.email)[0].toUpperCase()}
            </span>
            Sign out
          </button>
        </nav>
      )}

    </div>
  );
}

export default App;
